import { useQuery } from '@apollo/client';
import { Fragment, useEffect, useMemo, useReducer } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { MyTeamsQuery } from '../../queries';
import CapexIcon from '../icons/CapexIcon';
import CardIcon from '../icons/CardIcon';
import ChevronIcon from '../icons/ChevronIcon';
import CropTemplateIcon from '../icons/CropTemplateIcon';
import GearIcon from '../icons/GearIcon';
import InputIcon from '../icons/InputIcon';
import LocationIcon from '../icons/LocationIcon';
import TeamIcon from '../icons/TeamIcon';
import { PanelLink, PanelSection } from '../SidePanel';
import { IconSpan, NavMainItem, StickyHeader } from './menu-components';
import { alphanumericSort } from '../../utils/utils';

const toggleTeamReducer = (state, id) => {
  return {
    ...state,
    [id]: !state[id],
  };
};

const TeamLinkItem = ({ teamId, path, icon, name }) => {
  const location = useLocation();
  const teamPath = `/app/team/${teamId}/${path}`;
  const isActive = location.pathname.startsWith(teamPath);
  return (
    <NavMainItem isActive={isActive}>
      <PanelLink to={teamPath} isActive={isActive}>
        <IconSpan className="ml-4 d-flex justify-content-center">
          {icon}
        </IconSpan>
        {name}
      </PanelLink>
    </NavMainItem>
  );
};

const TeamsTree = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { data } = useQuery(MyTeamsQuery);

  const [toggleState, dispatchToggle] = useReducer(toggleTeamReducer, {});

  useEffect(() => {
    // Can't get the id from useParams because this isn't a subcomponent
    const currentId = location.pathname.split('/')[3];
    if (
      location.pathname.startsWith(`/app/team/`) &&
      currentId &&
      !toggleState[currentId]
    ) {
      dispatchToggle(currentId);
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const { teams = [] } = data?.me || {};
  const sortedTeams = useMemo(
    () => [...teams].sort((a, b) => alphanumericSort(a.name, b.name)),
    [teams],
  );

  return (
    <PanelSection>
      <StickyHeader>Teams</StickyHeader>
      <ul>
        <NavMainItem
          onClick={() => navigate('/app/onboard')}
          className="text-muted"
        >
          <IconSpan>
            <span>+</span>
          </IconSpan>
          Create New Team
        </NavMainItem>
        {sortedTeams.map(
          ({
            id,
            name,
            allowedActions,
            noSubscriptionNeeded,
            hasSubscription,
            hasValidSubscription,
          }) => {
            const onClick = () => {
              if (!toggleState[id]) {
                navigate(`/app/team/${id}/farms`);
              }
              dispatchToggle(id);
            };

            return (
              <Fragment key={id}>
                <NavMainItem onClick={onClick}>
                  <IconSpan>
                    <span>
                      <ChevronIcon
                        direction={toggleState[id] ? 'down' : 'right'}
                        className="w-100"
                      />
                    </span>
                  </IconSpan>
                  {name}
                </NavMainItem>
                {toggleState[id] && (
                  <>
                    <TeamLinkItem
                      teamId={id}
                      path="farms"
                      icon={<LocationIcon />}
                      name="Projects"
                    />
                    {allowedActions.canEdit && hasValidSubscription && (
                      <>
                        <TeamLinkItem
                          teamId={id}
                          path="templates"
                          icon={<CropTemplateIcon />}
                          name="Crop Templates"
                        />
                        <TeamLinkItem
                          teamId={id}
                          path="inputs"
                          icon={<InputIcon />}
                          name="Materials & Fuel"
                        />
                        <TeamLinkItem
                          teamId={id}
                          path="capexes"
                          icon={<CapexIcon />}
                          name="Equipment & Infrastructure"
                        />
                      </>
                    )}
                    <TeamLinkItem
                      teamId={id}
                      path="members"
                      icon={<TeamIcon />}
                      name="Team Members"
                    />
                    {allowedActions.canUpdateSettings && (
                      <>
                        <TeamLinkItem
                          teamId={id}
                          path="settings"
                          icon={<GearIcon />}
                          name="Team Settings"
                        />
                        {!noSubscriptionNeeded && (
                          <TeamLinkItem
                            teamId={id}
                            path={
                              hasSubscription
                                ? `billing?continueUrl=${window.location.href}`
                                : `onboard?backPath=${window.location.pathname}`
                            }
                            icon={<CardIcon />}
                            name="Billing"
                          />
                        )}
                      </>
                    )}
                  </>
                )}
              </Fragment>
            );
          },
        )}
      </ul>
    </PanelSection>
  );
};

export default TeamsTree;
